import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  LinearProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tooltip, InputAdornment,
} from '@mui/material';
import { Groups, Add, Edit, CheckCircle, Cancel as CancelIcon, Close, Send, Block } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const STATUS_CFG: Record<string, { color: any; label: string }> = {
  draft:     { color: 'default',   label: 'Draft' },
  pending:   { color: 'warning',   label: 'Pending' },
  approved:  { color: 'success',   label: 'Approved' },
  rejected:  { color: 'error',     label: 'Rejected' },
  filled:    { color: 'primary',   label: 'Filled' },
  cancelled: { color: 'default',   label: 'Cancelled' },
};

const TYPE_CFG: Record<string, string> = {
  new:        '🆕 New Role',
  backfill:   '🔄 Backfill',
  expansion:  '📈 Expansion',
  contractor: '🤝 Contractor',
};

const PRIORITY_CFG: Record<string, { color: any }> = {
  high:   { color: 'error' },
  medium: { color: 'warning' },
  low:    { color: 'default' },
};

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];
const LEVELS = ['junior', 'mid', 'senior', 'lead', 'manager', 'director'];

interface HCPlan { id: string; title: string; department?: string; entity?: string; type: string; status: string; priority?: string; headcount: number; targetYear?: number; targetQuarter?: string; budgetMin?: number; budgetMax?: number; currency?: string; jobLevel?: string; justification?: string; responsibilities?: string; requiredSkills?: string; filledCount: number; requestedBy?: { firstName: string; lastName: string }; approvedBy?: { firstName: string; lastName: string }; approvedAt?: string; rejectionReason?: string; notes?: string; }

const emptyForm = (): Partial<HCPlan> => ({
  type: 'new', priority: 'medium', headcount: 1, targetYear: new Date().getFullYear(), currency: 'HKD',
});

export default function Headcount() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');
  const isManager = isHR || user?.role === 'manager';

  const [tab, setTab] = useState(0);
  const [plans, setPlans] = useState<HCPlan[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState<Partial<HCPlan> | null>(null);
  const [approveDialog, setApproveDialog] = useState<{ plan: HCPlan; action: 'approve' | 'reject'; note: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [data, st] = await Promise.all([
        apiFetch(`/api/headcount?year=${yearFilter}${statusFilter ? `&status=${statusFilter}` : ''}`),
        apiFetch(`/api/headcount/stats?year=${yearFilter}`),
      ]);
      setPlans(data || []); setStats(st);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [yearFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!dialog) return;
    try {
      dialog.id
        ? await apiFetch(`/api/headcount/${dialog.id}`, { method: 'PATCH', body: JSON.stringify(dialog) })
        : await apiFetch('/api/headcount', { method: 'POST', body: JSON.stringify(dialog) });
      setDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const submit = async (id: string) => {
    try { await apiFetch(`/api/headcount/${id}/submit`, { method: 'PATCH' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const doApproval = async () => {
    if (!approveDialog) return;
    try {
      const ep = approveDialog.action === 'approve'
        ? `/api/headcount/${approveDialog.plan.id}/approve`
        : `/api/headcount/${approveDialog.plan.id}/reject`;
      const body = approveDialog.action === 'approve' ? { notes: approveDialog.note } : { reason: approveDialog.note };
      await apiFetch(ep, { method: 'PATCH', body: JSON.stringify(body) });
      setApproveDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const cancel = async (id: string) => {
    if (!window.confirm('Cancel this headcount request?')) return;
    try { await apiFetch(`/api/headcount/${id}/cancel`, { method: 'PATCH' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const pending = plans.filter(p => p.status === 'pending');

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Groups sx={{ fontSize: 32, color: '#1a237e' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Headcount Planning</Typography>
            <Typography variant="body2" color="text.secondary">Forecast and approve new hire requests</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Add />} sx={{ bgcolor: '#1a237e' }} onClick={() => setDialog(emptyForm())}>
          New Request
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Headcount', value: stats.totalHeadcount, color: '#1a237e' },
            { label: 'Approved', value: stats.totalApproved, color: '#2e7d32' },
            { label: 'Filled', value: stats.totalFilled, color: '#4a148c' },
            { label: 'Pending Review', value: stats.byStatus?.pending ?? 0, color: '#e65100' },
          ].map(s => (
            <Grid key={s.label} size={{ xs: 6, sm: 3 }}>
              <Card sx={{ textAlign: 'center', p: 1 }}>
                <CardContent sx={{ pb: '8px !important' }}>
                  <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="All Requests" />
          {isManager && <Tab label={`Pending Approval (${pending.length})`} />}
        </Tabs>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField label="Year" select size="small" value={yearFilter} onChange={e => setYearFilter(e.target.value)} sx={{ width: 100 }}>
          {['2024','2025','2026','2027'].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
        {['', 'draft', 'pending', 'approved', 'rejected', 'filled'].map(s => (
          <Chip key={s} label={s || 'All'} size="small"
            onClick={() => setStatusFilter(s)} color={statusFilter === s ? 'primary' : 'default'}
            variant={statusFilter === s ? 'filled' : 'outlined'} />
        ))}
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* ── TABLE ── */}
      {tab === 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><b>Role / Title</b></TableCell>
                <TableCell><b>Dept</b></TableCell>
                <TableCell><b>Type</b></TableCell>
                <TableCell><b>HC</b></TableCell>
                <TableCell><b>Target</b></TableCell>
                <TableCell><b>Budget</b></TableCell>
                <TableCell><b>Priority</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map(p => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>{p.title}</Typography>
                    {p.jobLevel && <Typography variant="caption" color="text.secondary">{p.jobLevel}</Typography>}
                  </TableCell>
                  <TableCell>{p.department ?? '—'}</TableCell>
                  <TableCell><Chip label={TYPE_CFG[p.type] ?? p.type} size="small" variant="outlined" /></TableCell>
                  <TableCell>
                    <Typography fontWeight={700}>{p.filledCount}/{p.headcount}</Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.targetQuarter ?? ''} {p.targetYear}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {p.budgetMin ? `HK$${Math.round(p.budgetMin / 1000)}k` : '—'}
                    {p.budgetMax ? `–${Math.round(p.budgetMax / 1000)}k` : ''}
                  </TableCell>
                  <TableCell>
                    {p.priority && <Chip label={p.priority} size="small" color={PRIORITY_CFG[p.priority]?.color ?? 'default'} />}
                  </TableCell>
                  <TableCell>
                    <Chip label={STATUS_CFG[p.status]?.label ?? p.status} size="small" color={STATUS_CFG[p.status]?.color} />
                    {p.rejectionReason && <Typography variant="caption" color="error" sx={{ display: 'block' }}>{p.rejectionReason}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => setDialog({ ...p })}><Edit fontSize="small" /></IconButton></Tooltip>
                      {p.status === 'draft' && <Tooltip title="Submit"><IconButton size="small" color="primary" onClick={() => submit(p.id)}><Send fontSize="small" /></IconButton></Tooltip>}
                      {isManager && p.status === 'pending' && <>
                        <Tooltip title="Approve"><IconButton size="small" color="success" onClick={() => setApproveDialog({ plan: p, action: 'approve', note: '' })}><CheckCircle fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Reject"><IconButton size="small" color="error" onClick={() => setApproveDialog({ plan: p, action: 'reject', note: '' })}><CancelIcon fontSize="small" /></IconButton></Tooltip>
                      </>}
                      {['draft','pending'].includes(p.status) && (
                        <Tooltip title="Cancel"><IconButton size="small" onClick={() => cancel(p.id)}><Block fontSize="small" /></IconButton></Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {plans.length === 0 && (
                <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No headcount requests for {yearFilter}.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── PENDING TAB ── */}
      {tab === 1 && isManager && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><b>Role</b></TableCell>
                <TableCell><b>Dept</b></TableCell>
                <TableCell><b>HC</b></TableCell>
                <TableCell><b>Requester</b></TableCell>
                <TableCell><b>Justification</b></TableCell>
                <TableCell><b>Budget</b></TableCell>
                <TableCell><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pending.map(p => (
                <TableRow key={p.id} hover>
                  <TableCell><Typography fontWeight={700}>{p.title}</Typography><Typography variant="caption" color="text.secondary">{TYPE_CFG[p.type]}</Typography></TableCell>
                  <TableCell>{p.department}</TableCell>
                  <TableCell><Typography fontWeight={700}>{p.headcount}</Typography></TableCell>
                  <TableCell>{p.requestedBy ? `${p.requestedBy.firstName} ${p.requestedBy.lastName}` : '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 200 }}><Typography variant="body2" noWrap>{p.justification ?? '—'}</Typography></TableCell>
                  <TableCell>{p.budgetMin ? `HK$${Math.round(p.budgetMin / 1000)}k` : '—'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button size="small" variant="contained" color="success" onClick={() => setApproveDialog({ plan: p, action: 'approve', note: '' })}>Approve</Button>
                      <Button size="small" color="error" onClick={() => setApproveDialog({ plan: p, action: 'reject', note: '' })}>Reject</Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {pending.length === 0 && <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No pending requests ✅</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── FORM DIALOG ── */}
      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialog?.id ? 'Edit Headcount Request' : 'New Headcount Request'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Role Title" fullWidth required value={dialog?.title ?? ''}
              onChange={e => setDialog(p => ({ ...p, title: e.target.value }))} />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="Department" fullWidth value={dialog?.department ?? ''}
                  onChange={e => setDialog(p => ({ ...p, department: e.target.value }))} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField label="Type" select fullWidth value={dialog?.type ?? 'new'}
                  onChange={e => setDialog(p => ({ ...p, type: e.target.value }))}>
                  {Object.entries(TYPE_CFG).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField label="Priority" select fullWidth value={dialog?.priority ?? 'medium'}
                  onChange={e => setDialog(p => ({ ...p, priority: e.target.value }))}>
                  <MenuItem value="high">🔴 High</MenuItem>
                  <MenuItem value="medium">🟡 Medium</MenuItem>
                  <MenuItem value="low">🟢 Low</MenuItem>
                </TextField>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid size={{ xs: 4 }}>
                <TextField label="Headcount" type="number" fullWidth value={dialog?.headcount ?? 1}
                  inputProps={{ min: 1 }} onChange={e => setDialog(p => ({ ...p, headcount: Number(e.target.value) }))} />
              </Grid>
              <Grid size={{ xs: 4 }}>
                <TextField label="Target Year" type="number" fullWidth value={dialog?.targetYear ?? new Date().getFullYear()}
                  onChange={e => setDialog(p => ({ ...p, targetYear: Number(e.target.value) }))} />
              </Grid>
              <Grid size={{ xs: 4 }}>
                <TextField label="Quarter" select fullWidth value={dialog?.targetQuarter ?? ''}
                  onChange={e => setDialog(p => ({ ...p, targetQuarter: e.target.value }))}>
                  <MenuItem value="">—</MenuItem>
                  {QUARTERS.map(q => <MenuItem key={q} value={q}>{q}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <TextField label="Budget Min" type="number" fullWidth value={dialog?.budgetMin ?? ''}
                  InputProps={{ startAdornment: <InputAdornment position="start">HK$</InputAdornment> }}
                  onChange={e => setDialog(p => ({ ...p, budgetMin: Number(e.target.value) }))} />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField label="Budget Max" type="number" fullWidth value={dialog?.budgetMax ?? ''}
                  InputProps={{ startAdornment: <InputAdornment position="start">HK$</InputAdornment> }}
                  onChange={e => setDialog(p => ({ ...p, budgetMax: Number(e.target.value) }))} />
              </Grid>
            </Grid>
            <TextField label="Job Level" select fullWidth value={dialog?.jobLevel ?? ''}
              onChange={e => setDialog(p => ({ ...p, jobLevel: e.target.value }))}>
              <MenuItem value="">—</MenuItem>
              {LEVELS.map(l => <MenuItem key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</MenuItem>)}
            </TextField>
            <TextField label="Justification" fullWidth multiline rows={2} value={dialog?.justification ?? ''}
              onChange={e => setDialog(p => ({ ...p, justification: e.target.value }))} />
            <TextField label="Key Responsibilities" fullWidth multiline rows={2} value={dialog?.responsibilities ?? ''}
              onChange={e => setDialog(p => ({ ...p, responsibilities: e.target.value }))} />
            <TextField label="Required Skills" fullWidth value={dialog?.requiredSkills ?? ''}
              onChange={e => setDialog(p => ({ ...p, requiredSkills: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="outlined" onClick={save} disabled={!dialog?.title}>Save Draft</Button>
          <Button variant="contained" sx={{ bgcolor: '#1a237e' }} onClick={async () => { await save(); if (dialog && !dialog.id) {} }}
            disabled={!dialog?.title}>
            {dialog?.id ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── APPROVE/REJECT DIALOG ── */}
      <Dialog open={!!approveDialog} onClose={() => setApproveDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{approveDialog?.action === 'approve' ? '✅ Approve' : '❌ Reject'} — {approveDialog?.plan.title}</DialogTitle>
        <DialogContent dividers>
          <TextField label={approveDialog?.action === 'approve' ? 'Notes (optional)' : 'Rejection Reason *'} fullWidth multiline rows={3}
            value={approveDialog?.note ?? ''}
            onChange={e => setApproveDialog(p => p ? { ...p, note: e.target.value } : null)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog(null)}>Back</Button>
          <Button variant="contained" color={approveDialog?.action === 'approve' ? 'success' : 'error'}
            onClick={doApproval}
            disabled={approveDialog?.action === 'reject' && !approveDialog?.note}>
            {approveDialog?.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
